import { WorkflowEdgeJSON, WorkflowJSON, WorkflowNodeJSON } from '@flowgram.ai/free-layout-editor';
import { Toast } from '@douyinfe/semi-ui';

import { FlowNodeJSON, JsonSchema, FlowDocumentJSON } from '../typings';
import { saveWorkflow } from '../https';
import { NodeFieldsEnum, DataSourceType } from '../nodes/constants';

export const getFieldValue = (key: string, form: any) => {
  const fields = form.getValueIn('fields') || [];
  if (!fields) {
    return '';
  }
  const field = fields.find((item: { key: string; value: string }) => item.key === key);
  return field?.value;
};

export const setFieldValue = (key: string, value: string, form: any) => {
  const fields = [...(form.getValueIn('fields') || [])];
  const fieldIndex = fields.findIndex((item: { key: string; value: string }) => item.key === key);
  if (fieldIndex > -1) {
    fields[fieldIndex] = { ...fields[fieldIndex], value };
  } else {
    fields.push({ key, value });
  }

  form.setValueIn('fields', fields);
};

/**
 * 按指定顺序创建节点及对应的关系，并插入到开始和结束节点中间
 * @param data - 包含 sql、js、feishuSheet 等数据的对象
 * @param startNodeId - 开始节点的 ID
 * @param endNodeId - 结束节点的 ID
 * @returns 更新后的 FlowDocumentJSON 数据
 */
export function createNodesInOrder(
  data: { sql?: string; js?: string; feishuSheet?: string },
  startNodeId: string,
  endNodeId: string
): FlowDocumentJSON {
  const initialData: FlowDocumentJSON = {
    nodes: [],
    edges: [],
  };

  const newNodes = [];
  const newEdges = [];
  let prevNodeId = startNodeId;

  // 创建 SQL 节点
  if (data.sql) {
    const sqlNode = {
      id: `sql_${Date.now()}`,
      type: 'sql',
      meta: { position: { x: 330, y: 381.75 } },
      data: {
        title: '执行SQL',
        fields: [
          { key: NodeFieldsEnum.sql, value: data.sql },
          { key: NodeFieldsEnum.dataSource, value: DataSourceType.BELINK },
        ],
      },
    };
    newNodes.push(sqlNode);
    newEdges.push({ sourceNodeID: prevNodeId, targetNodeID: sqlNode.id });
    prevNodeId = sqlNode.id;
  }

  // 创建 JS 节点
  if (data.js) {
    const jsNode = {
      id: `js_${Date.now()}`,
      type: 'js',
      meta: { position: { x: 480, y: 381.75 } },
      data: { title: '执行JS', fields: [{ key: 'jsCode', value: data.js }] },
    };
    newNodes.push(jsNode);
    newEdges.push({ sourceNodeID: prevNodeId, targetNodeID: jsNode.id });
    prevNodeId = jsNode.id;
  }

  // 创建飞书表格节点
  if (data.feishuSheet) {
    const feishuSheetNode = {
      id: `feishuSheet_${Date.now()}`,
      type: 'feishuSheet',
      meta: { position: { x: 630, y: 381.75 } },
      data: { title: '推飞书表格', fields: [{ key: 'link', value: data.feishuSheet }] },
    };
    newNodes.push(feishuSheetNode);
    newEdges.push({ sourceNodeID: prevNodeId, targetNodeID: feishuSheetNode.id });
    prevNodeId = feishuSheetNode.id;
  }

  // 连接最后一个新节点到结束节点
  if (newNodes.length > 0) {
    newEdges.push({ sourceNodeID: prevNodeId, targetNodeID: endNodeId });
  }

  return {
    ...initialData,
    nodes: [...initialData.nodes, ...newNodes],
    edges: [...initialData.edges, ...newEdges],
  };
}

export async function saveWorkflowFun(data: WorkflowJSON, taskId: string, executorName: string) {
  const { nodes, edges } = data;

  // 为同一上游的节点添加排序编号
  const addNumberToNodes = (nodes: WorkflowNodeJSON[], edges: WorkflowEdgeJSON[]) => {
    // 创建上游节点映射
    const upstreamMap = new Map<string, string[]>();

    edges.forEach((edge) => {
      const sourceId = edge.sourceNodeID;
      const targetId = edge.targetNodeID;

      if (!upstreamMap.has(sourceId)) {
        upstreamMap.set(sourceId, []);
      }
      upstreamMap.get(sourceId)!.push(targetId);
    });

    // 为每个节点添加排序编号
    return nodes.map((node: WorkflowNodeJSON) => {
      const config = {
        ...(node.data.fields || []).reduce(
          (acc: Record<string, any>, cur: { key: string; value: any }) => ({
            ...acc,
            [cur.key]: cur.value,
          }),
          {}
        ),
      };

      // 查找该节点的上游节点
      const upstreamNodeId = edges.find((edge) => edge.targetNodeID === node.id)?.sourceNodeID;

      if (upstreamNodeId && upstreamMap.has(upstreamNodeId)) {
        const downstreamNodes = upstreamMap.get(upstreamNodeId)!;
        const nodeIndex = downstreamNodes.indexOf(node.id);
        if (nodeIndex !== -1) {
          config.number = nodeIndex + 1; // 从1开始编号
        }
      }

      return {
        number: node.id,
        type: node.type,
        name: node.data.title,
        taskId,
        meta: { ...(node.meta?.position || {}) },
        config,
      };
    });
  };

  const taskNodes = addNumberToNodes(nodes, edges);
  const taskRelations = edges.map((edge: WorkflowEdgeJSON) => ({
    source: edge.sourceNodeID,
    target: edge.targetNodeID,
  }));

  try {
    const res = await saveWorkflow({
      taskId,
      executorName,
      taskNodes: taskNodes as ITaskNode[],
      nodeRelations: taskRelations as INodeRelation[],
    });
    Toast.success('保存成功');
    return res;
  } catch (error: any) {
    Toast.error(`保存失败: ${error.message || '未知错误'}`);
  }
}
